// components/Layout/Navbar.js
import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import "./Navbar.css";
import logoo from "./yupp-generated-image-699562-removebg-preview.png";

const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState({});
  const [isScrolled, setIsScrolled] = useState(false);
  const { user, logout, showLogin, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Cities for "Our Presence" dropdown
  const cities = [
    "Wardha",
    "Bhandara",
    "Gondia",
    "Nagpur",
    "Buldhana",
    "Amravati",
    "Yavatmal",
    "Washim",
    "Gadchiroli",
    "Akola",
    "Chandrapur",
  ];

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".dropdown")) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  // Close mobile menu when window resize above mobile
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 992) {
        setIsMobileMenuOpen(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setMobileDropdownOpen({});
    setActiveDropdown(null);
  }, [location.pathname]);

  const handleDropdownHover = (dropdown) => {
    if (window.innerWidth > 992) {
      setActiveDropdown(dropdown);
    }
  };

  const handleDropdownLeave = () => {
    if (window.innerWidth > 992) {
      setTimeout(() => {
        setActiveDropdown(null);
      }, 100);
    }
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    // Reset all mobile dropdowns when closing menu
    if (!isMobileMenuOpen) {
      setMobileDropdownOpen({});
    }
  };

  const toggleMobileDropdown = (dropdown) => {
    setMobileDropdownOpen(prev => ({
      ...prev,
      [dropdown]: !prev[dropdown]
    }));
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setMobileDropdownOpen({});
  };

  const closeAllMenus = () => {
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
    setMobileDropdownOpen({});
  };

  const handleNavLinkClick = () => {
    closeAllMenus();
  };

  const handleLogout = () => {
    logout();
    closeAllMenus();
    // Navigate to home page after logout
    navigate('/');
  };

  const handleLoginClick = () => {
    showLogin();
    closeAllMenus();
  };

  // Check if user is logged in
  const isLoggedIn = !!user;

  // If user is admin, redirect to admin panel and don't show navbar
  if (isAdmin && location.pathname !== '/admin' && location.pathname !== '/admin/*') {
    navigate('/admin');
    return null;
  }

  return (
    <nav className={`navbar ${isScrolled ? "scrolled" : ""}`}>
      <div className="navbar-container">
        {/* Logo - Left Aligned with Company Name */}
        <Link to="/" className="nav-logo" onClick={closeAllMenus}>
          <div className="logo-container">
            <img
              src={logoo}
              alt="Solar Energy Solutions"
              className="logo-image"
            />
            <span className="logo-text">Blue Sky Energy</span>
          </div>
        </Link>

        {/* Desktop Navigation - Center Aligned */}
        <div className="nav-center">
          <ul className="nav-menu">
            <li>
              <Link
                to="/"
                className={`nav-link ${
                  location.pathname === "/" ? "active" : ""
                }`}
                onClick={closeAllMenus}
              >
                Home
              </Link>
            </li>

            <li
              className="dropdown"
              onMouseEnter={() => handleDropdownHover("solutions")}
              onMouseLeave={handleDropdownLeave}
            >
              <button className="nav-link dropdown-toggle">
                Solutions <span className="dropdown-arrow">▼</span>
              </button>
              <div
                className={`dropdown-menu ${
                  activeDropdown === "solutions" ? "show" : ""
                }`}
              >
                <Link
                  to="/commercial"
                  className="dropdown-item"
                  onClick={closeAllMenus}
                >
                  Commercial
                </Link>
                <Link
                  to="/residential"
                  className="dropdown-item"
                  onClick={closeAllMenus}
                >
                  Residential
                </Link>
                <Link
                  to="/industrial"
                  className="dropdown-item"
                  onClick={closeAllMenus}
                >
                  Industrial
                </Link>
              </div>
            </li>

            <li
              className="dropdown"
              onMouseEnter={() => handleDropdownHover("about")}
              onMouseLeave={handleDropdownLeave}
            >
              <button className="nav-link dropdown-toggle">
                About Us <span className="dropdown-arrow">▼</span>
              </button>
              <div
                className={`dropdown-menu ${
                  activeDropdown === "about" ? "show" : ""
                }`}
              >
                <Link
                  to="/team"
                  className="dropdown-item"
                  onClick={closeAllMenus}
                >
                  Our Team
                </Link>
                <Link
                  to="/customers"
                  className="dropdown-item"
                  onClick={closeAllMenus}
                >
                  Our Customers
                </Link>
              </div>
            </li>

            <li>
              <Link
                to="/contact"
                className={`nav-link ${
                  location.pathname === "/contact" ? "active" : ""
                }`}
                onClick={closeAllMenus}
              >
                Contact Us
              </Link>
            </li>

            <li>
              <Link
                to="/products"
                className={`nav-link ${
                  location.pathname === "/products" ? "active" : ""
                }`}
                onClick={closeAllMenus}
              >
                Products
              </Link>
            </li>

            <li
              className="dropdown"
              onMouseEnter={() => handleDropdownHover("more")}
              onMouseLeave={handleDropdownLeave}
            >
              <button className="nav-link dropdown-toggle">
                More <span className="dropdown-arrow">▼</span>
              </button>
              <div
                className={`dropdown-menu dropdown-menu-wide ${
                  activeDropdown === "more" ? "show" : ""
                }`}
              >
                <Link
                  to="/news"
                  className="dropdown-item"
                  onClick={closeAllMenus}
                >
                  News
                </Link>
                <Link
                  to="/career"
                  className="dropdown-item"
                  onClick={closeAllMenus}
                >
                  Careers
                </Link>
                <Link
                  to="/blog"
                  className="dropdown-item"
                  onClick={closeAllMenus}
                >
                  Blog
                </Link>
                
                <div className="dropdown-divider"></div>
                <div className="dropdown-submenu">
                  <span className="dropdown-subheader">Our Presence</span>
                  <div className="cities-grid">
                    {cities.map((city, index) => (
                      <Link
                        key={index}
                        to={`/city/${city.toLowerCase()}`}
                        className="dropdown-item city-item"
                        onClick={closeAllMenus}
                      >
                        {city}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </li>
          </ul>
        </div>

        {/* Login/Profile - Right Aligned */}
        <div className="nav-right">
          <li
            className="dropdown profile-dropdown"
            onMouseEnter={() => handleDropdownHover("profile")}
            onMouseLeave={handleDropdownLeave}
          >
            <button className="nav-link profile-toggle">
              {isLoggedIn ? user?.email?.split("@")[0] || "Account" : "Login"}
            </button>
            <div
              className={`dropdown-menu ${
                activeDropdown === "profile" ? "show" : ""
              }`}
            >
              {isLoggedIn ? (
                <>
                  <div className="dropdown-user-info">
                    <div className="user-email-display">{user.email}</div>
                    {isAdmin && (
                      <div className="user-admin-badge">Administrator</div>
                    )}
                  </div>
                  <div className="dropdown-divider"></div>
                  <Link
                    to="/profile"
                    className="dropdown-item"
                    onClick={closeAllMenus}
                  >
                    My Profile
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="dropdown-item logout-button"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <button onClick={handleLoginClick} className="dropdown-item">
                    Login
                  </button>
                </>
              )}
            </div>
          </li>

          {/* Mobile Menu Button - Hamburger */}
          <button
            className={`mobile-menu-btn ${isMobileMenuOpen ? "active" : ""}`}
            onClick={toggleMobileMenu}
            aria-label="Toggle menu"
          >
            <span className="menu-icon"></span>
            <span className="menu-icon"></span>
            <span className="menu-icon"></span>
          </button>
        </div>
      </div>

      {/* Slide Down Mobile Menu - NOT a sidebar */}
      <div className={`mobile-slide-menu ${isMobileMenuOpen ? "open" : ""}`}>
        <div className="mobile-slide-menu-content">
          {/* Mobile Navigation Links - ALL with closeMobileMenu onClick */}
          <Link to="/" className="mobile-nav-link" onClick={closeMobileMenu}>
            Home
          </Link>

          {/* Solutions Dropdown */}
          <div className="mobile-dropdown-container">
            <button 
              className="mobile-dropdown-toggle"
              onClick={() => toggleMobileDropdown('solutions')}
            >
              Solutions
              <span className={`mobile-dropdown-arrow ${mobileDropdownOpen.solutions ? 'open' : ''}`}>▼</span>
            </button>
            <div className={`mobile-dropdown-content ${mobileDropdownOpen.solutions ? 'open' : ''}`}>
              <Link to="/commercial" className="mobile-dropdown-item" onClick={closeMobileMenu}>
                Commercial
              </Link>
              <Link to="/residential" className="mobile-dropdown-item" onClick={closeMobileMenu}>
                Residential
              </Link>
              <Link to="/industrial" className="mobile-dropdown-item" onClick={closeMobileMenu}>
                Industrial
              </Link>
            </div>
          </div>

          {/* About Us Dropdown */}
          <div className="mobile-dropdown-container">
            <button 
              className="mobile-dropdown-toggle"
              onClick={() => toggleMobileDropdown('about')}
            >
              About Us
              <span className={`mobile-dropdown-arrow ${mobileDropdownOpen.about ? 'open' : ''}`}>▼</span>
            </button>
            <div className={`mobile-dropdown-content ${mobileDropdownOpen.about ? 'open' : ''}`}>
              <Link to="/team" className="mobile-dropdown-item" onClick={closeMobileMenu}>
                Our Team
              </Link>
              <Link to="/customers" className="mobile-dropdown-item" onClick={closeMobileMenu}>
                Our Customers
              </Link>
            </div>
          </div>

          <Link to="/contact" className="mobile-nav-link" onClick={closeMobileMenu}>
            Contact Us
          </Link>

          <Link to="/products" className="mobile-nav-link" onClick={closeMobileMenu}>
            Products
          </Link>

          {/* More Dropdown */}
          <div className="mobile-dropdown-container">
            <button 
              className="mobile-dropdown-toggle"
              onClick={() => toggleMobileDropdown('more')}
            >
              More
              <span className={`mobile-dropdown-arrow ${mobileDropdownOpen.more ? 'open' : ''}`}>▼</span>
            </button>
            <div className={`mobile-dropdown-content ${mobileDropdownOpen.more ? 'open' : ''}`}>
              <Link to="/news" className="mobile-dropdown-item" onClick={closeMobileMenu}>
                News
              </Link>
              <Link to="/career" className="mobile-dropdown-item" onClick={closeMobileMenu}>
                Careers
              </Link>
              <Link to="/blog" className="mobile-dropdown-item" onClick={closeMobileMenu}>
                Blog
              </Link>
              <div className="mobile-dropdown-divider"></div>
              <div className="mobile-cities-section">
                <span className="mobile-cities-header">Our Presence</span>
                <div className="mobile-cities-grid">
                  {cities.map((city, index) => (
                    <Link
                      key={index}
                      to={`/city/${city.toLowerCase()}`}
                      className="mobile-city-item"
                      onClick={closeMobileMenu}
                    >
                      {city}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* User Info & Auth */}
          {isLoggedIn ? (
            <>
              <div className="mobile-user-info">
                <div className="mobile-user-email">{user.email}</div>
                {isAdmin && (
                  <div className="mobile-admin-badge">Administrator</div>
                )}
              </div>
              <Link
                to="/profile"
                className="mobile-nav-link"
                onClick={closeMobileMenu}
              >
                My Profile
              </Link>
              <button
                onClick={handleLogout}
                className="mobile-nav-link logout-btn"
              >
                Logout
              </button>
            </>
          ) : (
            <button onClick={handleLoginClick} className="mobile-nav-link login-btn">
              Login
            </button>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;